import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  adminCases,
  type EligibilityStatus,
  type CaseStatusAction,
  type AdminCaseEvent,
} from "@/lib/admin-endpoints";
import { StatusPill, EligibilityPill } from "@/components/AdminLayout";
import { cn } from "@/lib/utils";

const ELIGIBILITY_OPTIONS: { value: EligibilityStatus; label: string }[] = [
  { value: "eligible",      label: "✓ Eligible" },
  { value: "manual_review", label: "⚠ Manual review" },
  { value: "out_of_scope",  label: "✗ Out of scope" },
];

export function AdminCaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  // Eligibility override modal
  const [reviewOpen, setReviewOpen] = useState(false);
  const [newElig, setNewElig] = useState<EligibilityStatus>("eligible");
  const [reviewNote, setReviewNote] = useState("");
  const [reviewSuccess, setReviewSuccess] = useState(false);

  // Status action modal (approve / block)
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusAction, setStatusAction] = useState<CaseStatusAction>("approve");
  const [statusNotes, setStatusNotes] = useState("");
  const [statusSuccess, setStatusSuccess] = useState(false);

  const { data: c, isLoading, isError } = useQuery({
    queryKey: ["admin", "case", id],
    queryFn: () => adminCases.get(id!),
    enabled: !!id,
  });

  const reviewMutation = useMutation({
    mutationFn: () => adminCases.review(id!, { eligibility_status: newElig, note: reviewNote }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "case", id] });
      qc.invalidateQueries({ queryKey: ["admin", "cases"] });
      setReviewOpen(false);
      setReviewNote("");
      setReviewSuccess(true);
      setTimeout(() => setReviewSuccess(false), 3000);
    },
  });

  const statusMutation = useMutation({
    mutationFn: () => adminCases.status(id!, { action: statusAction, notes: statusNotes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "case", id] });
      qc.invalidateQueries({ queryKey: ["admin", "cases"] });
      setStatusOpen(false);
      setStatusNotes("");
      setStatusSuccess(true);
      setTimeout(() => setStatusSuccess(false), 3000);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-violet-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (isError || !c) {
    return (
      <div className="text-center py-24 text-slate-500 text-sm">
        Case not found or failed to load.
      </div>
    );
  }

  const fmt = (d?: string | null) =>
    d
      ? new Date(d).toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  // Cases that can be approved/blocked
  const canApprove = c.status === "finalized" || c.status === "in_review";
  const canBlock = c.status !== "closed";

  return (
    <div className="max-w-3xl">
      {/* Back */}
      <Link
        to="/admin/cases"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-violet-600 transition-colors mb-6"
      >
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        All cases
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">
            {c.case_type} Application
          </h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <StatusPill status={c.status} />
            <EligibilityPill status={c.eligibility_status} />
            {c.paid_at && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                ✓ Fee paid
              </span>
            )}
          </div>
          {reviewSuccess && (
            <p className="mt-2 text-xs text-emerald-600 font-medium">✓ Eligibility updated</p>
          )}
          {statusSuccess && (
            <p className="mt-2 text-xs text-emerald-600 font-medium">✓ Case status updated</p>
          )}
        </div>

        <div className="flex flex-col gap-2 self-start shrink-0">
          <button
            onClick={() => setReviewOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 text-xs font-semibold transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Override eligibility
          </button>
          {(canApprove || canBlock) && (
            <button
              onClick={() => {
                setStatusAction(canApprove ? "approve" : "block");
                setStatusOpen(true);
              }}
              className={cn(
                "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-semibold transition-all hover:-translate-y-0.5 hover:shadow-md",
                canApprove
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-rose-600 hover:bg-rose-700 text-white",
              )}
            >
              {canApprove ? "✓ Approve case" : "✗ Block case"}
            </button>
          )}
        </div>
      </div>

      {/* Owner card */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 mb-4">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Applicant</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Name" value={c.owner_name ?? "—"} />
          <Field label="Email" value={c.owner_email} />
          <Field label="User ID" value={c.owner_id} mono />
          <Field label="Applicant person ID" value={c.applicant_person_id ?? "—"} mono />
        </div>
      </div>

      {/* Case details */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 mb-4">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Case details</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Case ID" value={c.id} mono />
          <Field label="Case type" value={c.case_type} />
          <Field label="Status" value={c.status} />
          <Field label="Eligibility" value={c.eligibility_status ?? "—"} />
          <Field label="Created" value={fmt(c.created_at)} />
          <Field label="Updated" value={fmt(c.updated_at)} />
          <Field label="Paid at" value={fmt(c.paid_at)} />
          <Field label="Fee (cents)" value={c.fee_total_cents != null ? String(c.fee_total_cents) : "—"} />
          <Field label="Finalized" value={fmt(c.finalized_at)} />
          <Field label="Submitted" value={fmt(c.submitted_at)} />
        </div>
      </div>

      {/* Gating answers */}
      {c.gating_answers && Object.keys(c.gating_answers).length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 mb-4">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Gating answers</h2>
          <pre className="text-xs text-slate-600 bg-slate-50 rounded-xl p-4 overflow-auto whitespace-pre-wrap">
            {JSON.stringify(c.gating_answers, null, 2)}
          </pre>
        </div>
      )}

      {/* Event timeline */}
      {c.events && c.events.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 mb-4">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">Audit timeline</h2>
          <div className="space-y-3">
            {c.events.map((ev) => (
              <EventRow key={ev.id} event={ev} />
            ))}
          </div>
        </div>
      )}

      {/* Eligibility override modal */}
      {reviewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-base font-bold text-slate-900 mb-1">Override eligibility</h2>
            <p className="text-xs text-slate-500 mb-5">
              Case <span className="font-mono">{c.id.slice(0, 8)}…</span> · {c.owner_email}
            </p>

            <div className="space-y-2 mb-5">
              {ELIGIBILITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setNewElig(opt.value)}
                  className={cn(
                    "w-full text-left px-4 py-3 rounded-xl text-sm font-semibold border-2 transition-all",
                    newElig === opt.value
                      ? "border-violet-500 bg-violet-50 text-violet-800"
                      : "border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50",
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="mb-5">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Note <span className="font-normal text-slate-400">(optional — appears in audit log)</span>
              </label>
              <textarea
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                rows={3}
                placeholder="e.g. Manually verified green card copy on file"
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none transition-all"
              />
            </div>

            {reviewMutation.isError && (
              <p className="text-xs text-red-600 mb-4">Failed to save. Please try again.</p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setReviewOpen(false); reviewMutation.reset(); }}
                className="flex-1 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 py-2.5 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => reviewMutation.mutate()}
                disabled={reviewMutation.isPending}
                className="flex-1 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-sm font-semibold py-2.5 transition-all"
              >
                {reviewMutation.isPending ? "Saving…" : "Save decision"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Case status modal (approve / block) */}
      {statusOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-base font-bold text-slate-900 mb-1">
              {statusAction === "approve" ? "Approve case" : "Block case"}
            </h2>
            <p className="text-xs text-slate-500 mb-5">
              Case <span className="font-mono">{c.id.slice(0, 8)}…</span> · current status: <strong>{c.status}</strong>
            </p>

            <div className="flex gap-3 mb-5">
              {canApprove && (
                <button
                  onClick={() => setStatusAction("approve")}
                  className={cn(
                    "flex-1 rounded-xl border-2 px-4 py-3 text-sm font-semibold transition-all",
                    statusAction === "approve"
                      ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 text-slate-600 hover:border-slate-300",
                  )}
                >
                  ✓ Approve → submitted
                </button>
              )}
              {canBlock && (
                <button
                  onClick={() => setStatusAction("block")}
                  className={cn(
                    "flex-1 rounded-xl border-2 px-4 py-3 text-sm font-semibold transition-all",
                    statusAction === "block"
                      ? "border-rose-500 bg-rose-50 text-rose-800"
                      : "border-slate-200 text-slate-600 hover:border-slate-300",
                  )}
                >
                  ✗ Block → closed
                </button>
              )}
            </div>

            <div className="mb-5">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Notes <span className="font-normal text-slate-400">(optional — stored in audit log)</span>
              </label>
              <textarea
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                rows={3}
                placeholder="e.g. All documents verified, ready to submit"
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none transition-all"
              />
            </div>

            {statusMutation.isError && (
              <p className="text-xs text-red-600 mb-4">Failed to update status. Please try again.</p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setStatusOpen(false); statusMutation.reset(); }}
                className="flex-1 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 py-2.5 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => statusMutation.mutate()}
                disabled={statusMutation.isPending}
                className={cn(
                  "flex-1 rounded-xl text-white text-sm font-semibold py-2.5 transition-all disabled:opacity-60",
                  statusAction === "approve"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-rose-600 hover:bg-rose-700",
                )}
              >
                {statusMutation.isPending ? "Saving…" : statusAction === "approve" ? "Approve" : "Block"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">{label}</div>
      <div className={cn("text-sm text-slate-800 truncate", mono && "font-mono text-xs text-slate-500")}>
        {value}
      </div>
    </div>
  );
}

function EventRow({ event }: { event: AdminCaseEvent }) {
  const fmt = (d: string) =>
    new Date(d).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const isAdmin = event.actor_type === "admin";
  const dotColor = isAdmin ? "bg-violet-500" : "bg-slate-300";

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center pt-1">
        <div className={cn("w-2 h-2 rounded-full shrink-0", dotColor)} />
      </div>
      <div className="flex-1 min-w-0 pb-3 border-b border-slate-100 last:border-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className="text-xs font-semibold text-slate-700 font-mono">{event.event_type}</span>
          <span className="text-[10px] text-slate-400 shrink-0">{fmt(event.created_at)}</span>
        </div>
        {isAdmin && (
          <span className="inline-block text-[10px] font-medium bg-violet-50 text-violet-600 px-1.5 py-0.5 rounded mr-1">
            admin
          </span>
        )}
        {event.event_data && Object.keys(event.event_data).length > 0 && (
          <pre className="mt-1 text-[10px] text-slate-500 bg-slate-50 rounded-lg p-2 overflow-auto whitespace-pre-wrap">
            {JSON.stringify(event.event_data, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}
